import { differenceInYears, formatISO, intlFormat, isValid, parse, parseISO } from 'date-fns'

import { useDate } from '../Date'
import { Locale } from '../i18n'
interface useDobConfig {
  locale: Locale
  maxAge?: number
  minAge: number
}

interface DateOfBirth {
  formattedDate: string
  isoDate: string
  validity: InputBirthdayValidity
}
interface useDobHook {
  parseFormattedDateString: (input: string) => DateOfBirth
  parseISODateString: (input: string) => DateOfBirth
  dateFormat: string
}

export enum InputBirthdayValidity {
  Valid = 'valid',
  UnderAge = 'underAge',
  OverAge = 'overAge',
  InvalidDate = 'invalidDate',
  Undetermined = 'undertermined',
}

export enum InputBirthdayDateFormatPosition {
  PrimaryLabel = 'label',
  SecondaryLabel = 'secondaryLabel',
}

export const useDob = ({ locale = 'en-US', maxAge = 110, minAge = 18 }: useDobConfig): useDobHook => {
  const tempLocale = locale === 'en-AU' ? 'en-GB' : locale
  const { getLocalFormat } = useDate({
    locale,
    month: '2-digit',
    year: 'numeric',
    day: '2-digit',
  })
  const dateFormat = getLocalFormat()
  const validate = (inputDate: Date): InputBirthdayValidity => {
    const today = Date.now()
    const diff = differenceInYears(today, inputDate)
    const validAge = isValid(inputDate) && diff >= minAge && diff <= maxAge

    if (!validAge) {
      const invalid = !isValid(inputDate)
      const underAge = diff < minAge
      const overAge = diff > maxAge

      switch (true) {
        case underAge:
          return InputBirthdayValidity.UnderAge
        case overAge:
          return InputBirthdayValidity.OverAge
        case invalid:
          return InputBirthdayValidity.InvalidDate
        default:
          return InputBirthdayValidity.Undetermined
      }
    }

    return InputBirthdayValidity.Valid
  }

  const parseFormattedDateString = (input: string): DateOfBirth => {
    const date = parse(input, dateFormat.replace(/D/gi, 'd').replace(/Y/gi, 'y'), Date.now())
    const validity = validate(date)
    return {
      formattedDate: input,
      isoDate: isValid(date)
        ? formatISO(date, {
            representation: 'date',
          })
        : '',
      validity,
    }
  }

  const parseISODateString = (input: string): DateOfBirth => {
    const date = parseISO(input)
    const validity = validate(date)
    return {
      formattedDate: isValid(date)
        ? intlFormat(
            date,
            {
              month: '2-digit',
              year: 'numeric',
              day: '2-digit',
            },
            { locale: tempLocale }
          )
        : '',
      isoDate: input,
      validity,
    }
  }

  return { parseFormattedDateString, parseISODateString, dateFormat }
}
